import {
  Component, ViewEncapsulation, ViewChild, OnInit, DoCheck, Input, Output, EventEmitter, ElementRef, KeyValueDiffers
} from '@angular/core';
import { CfCoreComponent } from '../core/core.component';
import { GaugeModel } from '../../models/gauge/gauge.model';
import { GaugeStylingModel } from '../../models/gauge/gauge-styling.model';
import { GaugeTemplates } from '../../templates/gauge.template';
import { TemplateService } from '../../services/template-service/template.service';
/**
 * <p>Gauge component is a modification of
 * <a href="https://github.com/mattlewis92/angular-gauge" target="_blank">Angular gauge</a>
 * with added label, prefix and suffix elements.</p>
 * <pre>
 * <code>
 * import {{'{'}} GaugeModel {{'}'}} from 'cedrus-fusion'
 * import {{'{'}} GaugeStylingModel {{'}'}} from 'cedrus-fusion'
 * <code><</code>cf-gauge<code>></code><code><</code><code>/</code>cf-gauge<code>></code>
 * </code>
 * </pre>
 */
export class CfGaugeComponent extends CfCoreComponent implements OnInit, DoCheck {
/**
 * @hidden 
 * Object to register element state and compute differences.
 */
differ: any;
/**
 * @hidden 
 * It is a clone of gauge value to work with calculated effect during gauge animation time.
 * It is using only inside component functionality.
 */
_animatedValue: number;
  /* *@hidden
   * It is time of one point change of _animatedValue during animation time.
   */
  _tickInterval: number;
  /* *@hidden
   * It is increase or decrease of gauge value
   */
  _animationValueDirection: string;
/**
 * @hidden 
 * It is number of ticks between previous and current gauge value.
 */
_ticks: number;
/**
 * <pre>{{'{'}}
 *  display: boolean,           // Default: true.
 *  disable: boolean,           // Default: false.
 *  max: number,                // Maximum integer value of gauge. Default: 100.
 *  type: string,               // Type value of gauge. Can be: full, arch, semi. Default: full.
 *  size: number,               // Aanimation duration of gauge in seconds. Default: 150.
 *  value: number,              // Current integer value of gauge. Default: 0.
 *  label: string,              // Label of gauge. Empty string by default.
 *  prefix: string,             // Prefix of gauge. Empty string by default.
 *  suffix: string,             // Suffix of gauge. Empty string by default.
 *  inputValue: boolean,        // Able gauge to input it value. Default: true.
 *  animationDuration: number,  // Size for width/height of gauge. It means number of pixels. Default: 1.
 * {{'}'}}</pre>
 */
properties: GaugeModel;
/**
 * <p> dynamicClass: function() -> string,  // Function that returns name of the class </p>
 * <p> class: string                        // Name of the css class selector </p>
 * <pre>
 * {{'{'}}
 *  // Component container
 *  container: {{'{'}} class, dynamicClass {{'}'}},
 *  // Gauge svg element
 *  gauge: {{'{'}} class, dynamicClass {{'}'}},
 *  // Value styling
 *  value: {{'{'}} class, dynamicClass {{'}'}},
 *  // Prefix styling
 *  prefix: {{'{'}} class, dynamicClass {{'}'}},
 *  // Suffix styling
 *  suffix: {{'{'}} class, dynamicClass {{'}'}},
 *  // Label styling
 *  label: {{'{'}} class, dynamicClass {{'}'}},
 * </pre>
 */
styling: GaugeStylingModel;
/**
 * Maximum integer value of gauge. Default: 100.
 * @param {?} value
 * @return {?}
 */
set max(value: number) { this.properties.max = value; }
/**
 * Type value of gauge. Can be: full, arch, semi. Default is full.
 * @param {?} value
 * @return {?}
 */
set type(value: string) { this.properties.type = value; }
/**
 * Size for width/height of gauge. It means number of pixels. Default: 150.
 * @param {?} value
 * @return {?}
 */
set size(value: number) { this.properties.size = value; }
/**
 * @hidden 
 * Helper property for set value before ngOnInit and change after
 */
withInputValue = true;
/**
 * @hidden 
 * Helper property check for first value set
 */
firstValueChange = true;
/**
 * Current integer value of gauge. Default: 0.
 * @return {?}
 */
get value() {
    return this.elementRef.nativeElement.value;
  }
/**
 * @hidden 
 * Setter for the gauge value
 * @param {?} val
 * @return {?}
 */
set value(val: number) {
    if (val !== this.value) {
      this.elementRef.nativeElement.value = val;
      if (this.properties) { this.properties.value = val; }
      if (!this.firstValueChange) { this.valueChange.emit(this.value); }
    }

    if (this.firstValueChange) {
      if (!this.withInputValue) { this.valueChange.emit(this.value); }
      this.firstValueChange = false;
    }
  }
/**
 * Label of gauge. Empty string by default.
 * @param {?} value
 * @return {?}
 */
set label(value: string) { this.properties.label = value; }
/**
 * Prefix of gauge. Empty string by default.
 * @param {?} value
 * @return {?}
 */
set prefix(value: string) { this.properties.prefix = value; }
/**
 * Suffix of gauge. Empty string by default.
 * @param {?} value
 * @return {?}
 */
set suffix(value: string) { this.properties.suffix = value; }
/**
 * Able gauge to input it value. Default: true.
 * @param {?} value
 * @return {?}
 */
set inputValue(value: boolean) { this.properties.inputValue = value; }
/**
 * Aanimation duration of gauge in seconds. Default: 1.
 * @param {?} value
 * @return {?}
 */
set animationDuration(value: number) { this.properties.animationDuration = value; }
/**
 * @hidden 
 * Event emiter for updating value input property
 */
valueChange = new EventEmitter();
/**
 * <p>Event emited on value change and output value with type number</p>
 */
onChange: EventEmitter<any> = new EventEmitter<any>();
/**
 * @hidden 
 * It is selected type of gauge. It consists of start/end coordinates for svg elements.
 */
_type = { start: -90, end: -90.001 };
/**
 * @hidden 
 * It is an object with all gauges types and their start/end coordinates.
 */
_types = {
    'full': { start: -90, end: -90.001 },
    'arch': { start: 135, end: 45 },
    'semi': { start: 180, end: 0 },
  };
/**
 * @hidden 
 * It is mode in what gauge it is now.
 * Can be: displaying (standard to display), editing (user is changing value from input field),
 * clicks (user is changing value by clicking on svg element).
 */
_mode = 'displaying';
/**
 * @hidden 
 * It is half of properties size value and it is using to calculate circle values for x axis
 */
_centerX: number;
/**
 * @hidden 
 * It is half of properties size value and it is using to calculate circle values for y axis
 */
_centerY: number;
/**
 * @hidden 
 * It is ratio of user clicked point to properties.max value
 */
_clickedRatio: number;
/**
 * @hidden 
 * It is text value in span tag
 */
valueToShow;
/**
 * @hidden 
 * It is container with md-input-container to trigger input state/view changes
 */
valueToEditContainer;
/**
 * @hidden 
 * It is original input field
 */
valueToEdit;
/**
 * @hidden 
 * Function to calculate valueToEditContainer width according to input width
 * @return {?}
 */
_setInputWidth() {
    setTimeout(() => {
      this.valueToEditContainer.nativeElement.style.width = (this.valueToShow.nativeElement.clientWidth + 2) + 'px';
    }, 0);
  }
/**
 * @hidden 
 * Method to set gauge value when user is changing it from input field
 * @return {?}
 */
_changeGaugeValue() {
    if (this.properties.value <= 0) { this.properties.value = 0; }
    if (this.properties.value >= this.properties.max) { this.properties.value = this.properties.max; }
    this._animatedValue = this.properties.value;
    this.value = this.properties.value;
    this._setInputWidth();
  }
/**
 * @hidden 
 * Method that shows input field after user click on gauge value number. In that moment span with gauge value is hidden.
 * @return {?}
 */
_showInput() {
    if (!this.properties.disable && this.properties.inputValue) {
      this._mode = 'editing';
      setTimeout(() => {
        this.valueToEdit.nativeElement.focus();
      }, 0);
      this.valueToEditContainer.nativeElement.style.width = (this.valueToShow.nativeElement.clientWidth + 2) + 'px';
    }
  }
/**
 * @hidden 
 * It is function to increase / decrease gauge value from previous to current during animation time. Used only inside component logic.
 * @return {?}
 */
private _animatingValue() {
    if (this._ticks > 0) {
      setTimeout(() => {
        if (this._animationValueDirection === 'increase') {
          ++this._animatedValue;
        } else {
          --this._animatedValue;
        }
        --this._ticks;
        this._animatingValue();
      }, this._tickInterval);
    }
  }
/**
 * @hidden 
 * It is function to recalculate value if it was changed during animation time. Used only inside component logic.
 * @param {?} prev  previous value
 * @param {?} curr  current value
 * @return {?}
 */
private _animateValueChange(prev, curr) {
    if (this.properties) {
      this._ticks = Math.abs(curr - prev);
      this._tickInterval = Math.ceil(this.properties.animationDuration * 1000 / this._ticks);
      this._animationValueDirection = curr > prev ? 'increase' : 'decrease';
      this._animatingValue();
    }
  }
/**
 * @hidden
 * @param {?} eX
 * @param {?} eY
 * @return {?}
 */
private _pickFullTypeValue(eX, eY) {
    let /** @type {?} */ eXCentered = eX > this._centerX ? eX - this._centerX : this._centerX - eX, /** @type {?} */
      eYCentered = eY > this._centerY ? eY - this._centerY : this._centerY - eY, /** @type {?} */
      clickedRarius = Math.sqrt(Math.pow(eXCentered, 2) + Math.pow(eYCentered, 2)), /** @type {?} */
      circuit = 2 * Math.PI * clickedRarius, /** @type {?} */
      clickedAngle;
    if (eX <= this._centerX && eY <= this._centerY) {
      clickedAngle = 270 + Math.acos(eXCentered / clickedRarius) / (Math.PI / 180);
    } else if (eX > this._centerX && eY <= this._centerY) {
      clickedAngle = Math.acos(eYCentered / clickedRarius) / (Math.PI / 180);
    } else if (eX >= this._centerX && eY >= this._centerY) {
      clickedAngle = 90 + Math.acos(eXCentered / clickedRarius) / (Math.PI / 180);
    } else {
      clickedAngle = 180 + Math.acos(eYCentered / clickedRarius) / (Math.PI / 180);
    }
    this._clickedRatio = ((Math.PI * clickedRarius * clickedAngle) / 180) / circuit;
    this.properties.value = Number((this._clickedRatio * this.properties.max).toFixed());
    this._animatedValue = this.properties.value;
    this.value = this.properties.value;
  }
/**
 * @hidden
 * @param {?} eX
 * @param {?} eY
 * @return {?}
 */
private _pickArchTypeValue(eX, eY) {
    if (eY < this.properties.size * 0.85) {
      let /** @type {?} */ eXCentered = eX > this._centerX ? eX - this._centerX : this._centerX - eX, /** @type {?} */
        eYCentered = eY > this._centerY ? eY - this._centerY : this._centerY - eY, /** @type {?} */
        clickedRarius = Math.sqrt(Math.pow(eXCentered, 2) + Math.pow(eYCentered, 2)), /** @type {?} */
        circuit = 1.5 * Math.PI * clickedRarius, /** @type {?} */
        clickedAngle;
      if (eX <= this._centerX && eY <= this._centerY) {
        clickedAngle = 45 + Math.acos(eXCentered / clickedRarius) / (Math.PI / 180);
      } else if (eX > this._centerX && eY <= this._centerY) {
        clickedAngle = 135 + Math.acos(eYCentered / clickedRarius) / (Math.PI / 180);
      } else if (eX >= this._centerX && eY >= this._centerY) {
        clickedAngle = 225 + Math.acos(eXCentered / clickedRarius) / (Math.PI / 180);
      } else {
        clickedAngle = Math.acos(eYCentered / clickedRarius) / (Math.PI / 180) - 45;
      }
      if (clickedAngle >= 0 && clickedAngle <= 270) {
        this._clickedRatio = ((Math.PI * clickedRarius * clickedAngle) / 180) / circuit;
        this.properties.value = Number((this._clickedRatio * this.properties.max).toFixed());
        this._animatedValue = this.properties.value;
        this.value = this.properties.value;
      }
    }
  }
/**
 * @hidden
 * @param {?} eX
 * @param {?} eY
 * @return {?}
 */
private _pickSemiTypeValue(eX, eY) {
    if (eY <= this._centerY) {
      let /** @type {?} */ eXCentered = eX > this._centerX ? eX - this._centerX : this._centerX - eX, /** @type {?} */
        eYCentered = this._centerY - eY, /** @type {?} */
        clickedRarius = Math.sqrt(Math.pow(eXCentered, 2) + Math.pow(eYCentered, 2)), /** @type {?} */
        circuit = Math.PI * clickedRarius, /** @type {?} */
        clickedAngle;
      if (eX <= this._centerX) {
        clickedAngle = Math.acos(eXCentered / clickedRarius) / (Math.PI / 180);
      } else {
        clickedAngle = 90 + Math.acos(eYCentered / clickedRarius) / (Math.PI / 180);
      }
      if (clickedAngle >= 0 && clickedAngle <= 180) {
        this._clickedRatio = ((Math.PI * clickedRarius * clickedAngle) / 180) / circuit;
        this.properties.value = Number((this._clickedRatio * this.properties.max).toFixed());
        this._animatedValue = this.properties.value;
        this.value = this.properties.value;
      }
    }
  }
/**
 * @hidden 
 * Method called after user click on gauge svg element with offsetX and offsetY values of clicked $event
 * @param {?} eX
 * @param {?} eY
 * @return {?}
 */
private _pickValue(eX, eY) {
    if (!this.properties.disable && this.properties.inputValue) {
      this._mode = 'displaying';
      if (this.properties.type === 'full') { this._pickFullTypeValue(eX, eY); }
      if (this.properties.type === 'arch') { this._pickArchTypeValue(eX, eY); }
      if (this.properties.type === 'semi') { this._pickSemiTypeValue(eX, eY); }
    }
  }
/**
 * @hidden
 * @param {?} elementRef
 * @param {?} templateService
 * @param {?} differs
 */
constructor(public elementRef: ElementRef, /**@hidden */ templateService: TemplateService, differs: KeyValueDiffers) {
    super(elementRef, templateService);
    this.differ = differs.find({}).create(null);
    this.properties = new GaugeModel();
    this.styling = new GaugeStylingModel();
  }
/**
 * @hidden
 * @return {?}
 */
ngOnInit() {
    this.getMyTemplate('gauge', GaugeTemplates).then(() => {
      this.mergeProperties(this.activeTemplate.property, this.properties, 'GAUGE').then( sampler => {
        let /** @type {?} */ active = new GaugeStylingModel(this.activeTemplate.style);
        this.styling = Object.assign(active, this.styling);
        this.value != null ? this.properties.value = this.value : this.withInputValue = false;
        this._centerX = this.properties.size / 2;
        this._centerY = this.properties.size / 2;
        this._animatedValue = this.properties.value;
        if (this.properties.type) {
          switch (this.properties.type) {
            case 'arch': this._type = this._types.arch; break;
            case 'semi': this._type = this._types.semi; break;
          }
        }
        // console.log('GAUGE', this);
      });
    });
  }
/**
 * @hidden 
 * <p> Update the properties object with the changes on the component inner properties to keep the component dynamic.</p>
 * @return {?}
 */
ngDoCheck() {
    if (this.differ) {
      const /** @type {?} */ changes = this.differ.diff(this.properties);
      if (changes) {
        changes.forEachChangedItem((record) => {
          if (record.key === 'value') {
            if (this._mode === 'displaying') {
              this._animatedValue = record.previousValue;
              this._animateValueChange(record.previousValue, record.currentValue);
              this.value = record.currentValue;
            }
          }
        });
      }
    }
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  selector: 'cf-gauge',
  encapsulation: ViewEncapsulation.None,
  template: "<div *ngIf=\"_inited && properties?.display\" class=\"cf-gauge-container {{styling.container.class}} {{properties.disable ? 'disabled':''}}\" [ngClass]=\"getDynamicClasses(styling?.container?.dynamicClass)\"> <div class=\"core-gauge-platform\"> <mwl-gauge class=\"{{styling.gauge.class}}\" [ngClass]=\"getDynamicClasses(styling?.gauge?.dynamicClass)\" [max]=\"properties.max\" [value]=\"properties.value\" [animated]=\"true\" [showValue]=\"false\" [dialClass]=\"'cf-gauge-bar'\" [gaugeClass]=\"'cf-gauge'\" [dialEndAngle]=\"_type.end\" [valueDialClass]=\"'cf-gauge-track'\" [valueTextClass]=\"'cf-gauge-value-hidden'\" [dialStartAngle]=\"_type.start\" [animationDuration]=\"properties.animationDuration\" [ngStyle]=\"{'width': properties.size + 'px', 'height': properties.size + 'px'}\" (click)=\"_pickValue($event.offsetX, $event.offsetY)\"></mwl-gauge> <div class=\"core-gauge-value-platform\"> <div class=\"cf-gauge-value-container\"> <span class=\"cf-gauge-prefix {{styling.prefix.class}}\" [ngClass]=\"getDynamicClasses(styling?.prefix?.dynamicClass)\">{{properties.prefix}}</span> <span class=\"cf-gauge-value {{styling.value.class}}\" [ngClass]=\"getDynamicClasses(styling?.value?.dynamicClass)\"> <span #valueToShow [ngClass]=\"{'editing' : _mode==='editing'}\" (click)=\"_showInput($event)\"> {{_animatedValue}} </span> <span [hidden]=\"!(_mode==='editing')\" #valueToEditContainer style=\"overflow:hidden\"> <md-input-container> <input #valueToEdit mdInput [disabled]=\"properties.disable\" type=\"number\" [(ngModel)]=\"properties.value\" (ngModelChange)=\"_changeGaugeValue($event)\" (blur)=\"_mode = 'displaying'\" attr.min=\"0\" attr.max=\"{{properties.max}}\"> </md-input-container> </span> </span> <span class=\"cf-gauge-suffix {{styling.suffix.class}}\" [ngClass]=\"getDynamicClasses(styling?.suffix?.dynamicClass)\">{{properties.suffix}}</span> </div> <div class=\"core-gauge-label-platform\"> <span class=\"cf-gauge-label {{styling.label.class}}\" [ngClass]=\"getDynamicClasses(styling?.label?.dynamicClass)\">{{properties.label}}</span> </div> </div> </div> </div> ",
  styles: [".cf-gauge-container { display: inline-flex; position: relative; z-index: 0; } .cf-gauge-container > div.core-gauge-platform { position: relative; z-index: 0; } .cf-gauge-container mwl-gauge { display: block; cursor: pointer; } .cf-gauge-container mwl-gauge .cf-gauge .cf-gauge-value-hidden { display: none !important; } .cf-gauge-container.disabled mwl-gauge { cursor: default; } .cf-gauge-container.disabled mwl-gauge .cf-gauge-track { stroke: darkgray; } .cf-gauge-container.disabled .cf-gauge-prefix, .cf-gauge-container.disabled .cf-gauge-suffix, .cf-gauge-container.disabled .cf-gauge-value, .cf-gauge-container.disabled .cf-gauge-label { color: darkgray; } .core-gauge-value-platform { position: absolute; z-index: 1; top: 40% !important; left: 50% !important; width: 0px !important; display: flex; flex-direction: column; justify-content: center; align-items: center; color: #009688; } .cf-gauge-value-container { position: relative; z-index: 0; display: flex; flex-direction: row; justify-content: center; align-items: baseline; } .cf-gauge-bar { stroke: #e0e0e0; stroke-width: 4%; fill: transparent; } .cf-gauge-track { stroke: #009688; stroke-width: 8%; fill: transparent; } .cf-gauge-prefix, .cf-gauge-suffix { font-size: 14px; } .cf-gauge-value { display: inline-flex; align-items: baseline; font-size: 26px; } .cf-gauge-value md-input-container { overflow: hidden; margin-bottom: -7px !important; } .cf-gauge-value md-input-container .mat-input-wrapper { margin: 0 !important; padding-bottom: 0 !important; } .cf-gauge-value md-input-container .mat-input-infix { padding: 0 !important; border-top: none !important; } .cf-gauge-value md-input-container .mat-input-placeholder-wrapper { top: 0 !important; padding-top: 0 !important; } .cf-gauge-value md-input-container input { padding-right: 30px !important; width: 150% !important; } .cf-gauge-value md-input-container .mat-input-underline { bottom: 1px !important; } .cf-gauge-value md-input-container .mat-input-ripple { background-color: #009688; } .cf-gauge-value .editing { position: absolute; z-index: -1; opacity: 0 !important; } .core-gauge-label-platform { display: flex; justify-content: center; align-items: baseline; margin-top: 5px; } .cf-gauge-label { white-space: nowrap; font-size: 10px; } :host::ng-deep [hidden] { display: none !important; } @media screen and (-ms-high-contrast: active), screen and (-ms-high-contrast: none) { .cf-gauge-value md-input-container { margin-bottom: -6px !important; } } "]
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ElementRef, },
{type: TemplateService, },
{type: KeyValueDiffers, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'properties': [{ type: Input },],
'styling': [{ type: Input },],
'max': [{ type: Input, args: ['max', ] },],
'type': [{ type: Input, args: ['type', ] },],
'size': [{ type: Input, args: ['size', ] },],
'value': [{ type: Input },],
'label': [{ type: Input, args: ['label', ] },],
'prefix': [{ type: Input, args: ['prefix', ] },],
'suffix': [{ type: Input, args: ['suffix', ] },],
'inputValue': [{ type: Input, args: ['inputValue', ] },],
'animationDuration': [{ type: Input, args: ['animationDuration', ] },],
'valueChange': [{ type: Output },],
'onChange': [{ type: Output },],
'valueToShow': [{ type: ViewChild, args: ['valueToShow', ] },],
'valueToEditContainer': [{ type: ViewChild, args: ['valueToEditContainer', ] },],
'valueToEdit': [{ type: ViewChild, args: ['valueToEdit', ] },],
};
}

function CfGaugeComponent_tsickle_Closure_declarations() {
/** @type {?} */
CfGaugeComponent.decorators;
/**
 * @nocollapse
 * @type {?}
 */
CfGaugeComponent.ctorParameters;
/** @type {?} */
CfGaugeComponent.propDecorators;
/**
 * @hidden 
 * Object to register element state and compute differences.
 * @type {?}
 */
CfGaugeComponent.prototype.differ;
/**
 * @hidden 
 * It is a clone of gauge value to work with calculated effect during gauge animation time.
 * It is using only inside component functionality.
 * @type {?}
 */
CfGaugeComponent.prototype._animatedValue;
/** @type {?} */
CfGaugeComponent.prototype._tickInterval;
/** @type {?} */
CfGaugeComponent.prototype._animationValueDirection;
/**
 * @hidden 
 * It is number of ticks between previous and current gauge value.
 * @type {?}
 */
CfGaugeComponent.prototype._ticks;
/**
 * <pre>{{'{'}}
 *  display: boolean,           // Default: true.
 *  disable: boolean,           // Default: false.
 *  max: number,                // Maximum integer value of gauge. Default: 100.
 *  type: string,               // Type value of gauge. Can be: full, arch, semi. Default: full.
 *  size: number,               // Aanimation duration of gauge in seconds. Default: 150.
 *  value: number,              // Current integer value of gauge. Default: 0.
 *  label: string,              // Label of gauge. Empty string by default.
 *  prefix: string,             // Prefix of gauge. Empty string by default.
 *  suffix: string,             // Suffix of gauge. Empty string by default.
 *  inputValue: boolean,        // Able gauge to input it value. Default: true.
 *  animationDuration: number,  // Size for width/height of gauge. It means number of pixels. Default: 1.
 * {{'}'}}</pre>
 * @type {?}
 */
CfGaugeComponent.prototype.properties;
/**
 * <p> dynamicClass: function() -> string,  // Function that returns name of the class </p>
 * <p> class: string                        // Name of the css class selector </p>
 * <pre>
 * {{'{'}}
 *  // Component container
 *  container: {{'{'}} class, dynamicClass {{'}'}},
 *  // Gauge svg element
 *  gauge: {{'{'}} class, dynamicClass {{'}'}},
 *  // Value styling
 *  value: {{'{'}} class, dynamicClass {{'}'}},
 *  // Prefix styling
 *  prefix: {{'{'}} class, dynamicClass {{'}'}},
 *  // Suffix styling
 *  suffix: {{'{'}} class, dynamicClass {{'}'}},
 *  // Label styling
 *  label: {{'{'}} class, dynamicClass {{'}'}},
 * </pre>
 * @type {?}
 */
CfGaugeComponent.prototype.styling;
/**
 * @hidden 
 * Helper property for set value before ngOnInit and change after
 * @type {?}
 */
CfGaugeComponent.prototype.withInputValue;
/**
 * @hidden 
 * Helper property check for first value set
 * @type {?}
 */
CfGaugeComponent.prototype.firstValueChange;
/**
 * @hidden 
 * Event emiter for updating value input property
 * @type {?}
 */
CfGaugeComponent.prototype.valueChange;
/**
 * <p>Event emited on value change and output value with type number</p>
 * @type {?}
 */
CfGaugeComponent.prototype.onChange;
/**
 * @hidden 
 * It is selected type of gauge. It consists of start/end coordinates for svg elements.
 * @type {?}
 */
CfGaugeComponent.prototype._type;
/**
 * @hidden 
 * It is an object with all gauges types and their start/end coordinates.
 * @type {?}
 */
CfGaugeComponent.prototype._types;
/**
 * @hidden 
 * It is mode in what gauge it is now.
 * Can be: displaying (standard to display), editing (user is changing value from input field),
 * clicks (user is changing value by clicking on svg element).
 * @type {?}
 */
CfGaugeComponent.prototype._mode;
/**
 * @hidden 
 * It is half of properties size value and it is using to calculate circle values for x axis
 * @type {?}
 */
CfGaugeComponent.prototype._centerX;
/**
 * @hidden 
 * It is half of properties size value and it is using to calculate circle values for y axis
 * @type {?}
 */
CfGaugeComponent.prototype._centerY;
/**
 * @hidden 
 * It is ratio of user clicked point to properties.max value
 * @type {?}
 */
CfGaugeComponent.prototype._clickedRatio;
/**
 * @hidden 
 * It is text value in span tag
 * @type {?}
 */
CfGaugeComponent.prototype.valueToShow;
/**
 * @hidden 
 * It is container with md-input-container to trigger input state/view changes
 * @type {?}
 */
CfGaugeComponent.prototype.valueToEditContainer;
/**
 * @hidden 
 * It is original input field
 * @type {?}
 */
CfGaugeComponent.prototype.valueToEdit;
/** @type {?} */
CfGaugeComponent.prototype.elementRef;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
